import "./facets";
import "./filter-drawer";
import { initializeApiFacets } from "./api-facets";

import storefront from "../global/storefront";
import { forEachConnectionPage } from "../global/storefront/paginate";
import { COLLECTION_GROUPED_COUNT_QUERY, SEARCH_GROUPED_COUNT_QUERY } from "../global/storefront/queries";

import {
  runGroupingPass,
  verifyGrouping,
  pendingRevealAttr,
  hydrateApiProductCard,
  createGroupedCollectionCounter,
  createStyleGroupIndex,
  isExcludedProduct,
  isHomeTryOnType,
  isSwatchProductFromTags,
} from "./product-grouping";

import PLPInterstitial from "../plp-interstitial/plp-interstitial";

const defaultPageSize = 20;
const loadMoreChunkSize = 20;

const pageBatchSize = 6;

const maxAdaptivePageBatchSize = 8;

const maxInitialSeekPageBatchSize = 24;

const prefetchPagesAhead = 6;

const paginationWindow = 4;
const revealClass = "main-collection__product-card--revealing";
const groupedPageParam = "grouped_page";
const scrollDurationMs = 500;
// Filtered/search cards reveal in grid order as their images load; after this
// long the rest of the chunk shows anyway, so one slow image can't hold up
// every card after it.
const revealImageDeadlineMs = 600;
const serverCardSectionId = "product-card-render";

const groupedCountTtlMs = 30 * 60 * 1000;
const groupedCountPersistentTtlMs = 6 * 60 * 60 * 1000;

const maxGroupedCountPages = 200;

// First load waits at most this long for the filters before showing the grid.
const firstPaintMaxWaitMs = 1200;

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function animateScrollTo(targetY, duration = scrollDurationMs) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo(0, targetY);

    return;
  }

  const startY = window.scrollY;
  const distance = targetY - startY;
  const startTime = performance.now();

  function step(now) {
    const progress = Math.min((now - startTime) / duration, 1);

    window.scrollTo(0, startY + distance * easeInOutCubic(progress));

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

class MainCollection extends HTMLElement {
  constructor() {
    super();

    this.loading = false;
    this.apiFilterMode = false;
    this.loadingMode = "pagination";
    this.infiniteVisibleCount = 0;
    this.infiniteObserver = null;
    this.renderToken = 0;
    this.plpInterstitial = null;
    this.suppressInterstitials = false;

    this.groupedCountCache = new Map();
    this.groupedCountPromises = new Map();
    this.groupedCountToken = 0;

    this.hasResolvedProductCount = false;
    this.gridGroupingSettled = false;

    this.resolvedGroupedProductCount = null;
    this.resolvedFilteredGroupedProductCount = null;
    this.activeFilteredCountKey = null;

    this.consumeChain = Promise.resolve();

    this.apiRenderChain = Promise.resolve();

    this.synchronizedUpdateDepth = 0;
    this.initialReady = false;

    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.productGrid = this.querySelector("[data-product-grid]");

    if (!this.productGrid) {
      return;
    }

    this.defaultProductGridHtml = this.productGrid.innerHTML;

    // Template for cards built from API results. Taken from a regular product
    // where there is one: a Home Try-On card carries its own product's size
    // panel variants, and a swatch card its own add-to-bag, which a clone for
    // another product would keep. Those products get their real server-rendered
    // card instead (see insertApiProductCards).
    const templateCard =
      this.productGrid.querySelector(
        '[data-product-card]:not([data-home-try-on="true"]):not([data-swatch-product="true"])',
      ) || this.productGrid.querySelector("[data-product-card]");

    this.apiProductCardTemplate = templateCard ? templateCard.cloneNode(true) : null;

    this.pageSize = Number.parseInt(this.dataset.pageSize, 10) || defaultPageSize;
    this.loadingMode = this.dataset.loadingMode === "infinite" ? "infinite" : "pagination";

    this.collectionHandle = this.dataset.collectionHandle || null;

    this.searchQuery = this.dataset.searchQuery || null;

    this.isAllProductsCollection =
      !this.searchQuery &&
      String(this.collectionHandle || "")
        .trim()
        .toLowerCase() === "all";

    // "all" has no collection(handle:) on the Storefront API, so it (like
    // search) looks group members up by tag store-wide.
    this.styleGroupIndex = createStyleGroupIndex({
      collectionHandle: this.searchQuery || this.isAllProductsCollection ? null : this.collectionHandle,
    });

    this.syncFromDom();
    this.createFirstPaintBarrier();

    this.addEventListener("api-facets:loading", (event) => {
      // Do not suppress interstitials here. This event also fires during the
      // initial/default facets request, before we know whether a real filter
      // or sort is active. Suppression is applied only when API results are
      // confirmed as filtered/sorted in handleApiFilteredProducts().
      //
      // A filter change (or clearing one) does replace the grid, though: show
      // that straight away instead of leaving the old results up until the
      // response arrives.
      if (event.detail?.hasActiveFilters || this.apiFilterMode) {
        this.showApiLoadingSkeleton(loadMoreChunkSize);
      }
    });

    // The request failed or returned nothing to render.
    this.addEventListener("api-facets:failed", () => {
      this.hideApiLoadingSkeleton();
    });

    this.addEventListener("api-facets:products", (event) => {
      const detail = event.detail || {};

      this.enqueueApiFilteredProducts(detail).finally(() => {
        detail.resolveRender?.();
      });
    });

    window.initializeProductCardInteractions(this.productGrid);

    this.initializeCollectionPage();

    this.addEventListener("click", this.handleClick);
    this.setupInfiniteScroll();
  }

  // The filter sidebar and product grid appear as soon as their initial data is
  // ready. Swatches and Product Group verification continue asynchronously so
  // Storefront API requests don't delay the initial product-card render.
  createFirstPaintBarrier() {
    this.firstPaintDone = Boolean(this.searchQuery);

    if (this.firstPaintDone) {
      this.firstPaintParts = new Map();
      this.firstPaintPromise = Promise.resolve();

      return;
    }

    this.firstPaintParts = new Map(
      ["filters", "grid"].map((part) => {
        let resolve;
        const promise = new Promise((done) => {
          resolve = done;
        });

        return [part, { promise, resolve }];
      }),
    );

    const cap = new Promise((resolve) => {
      setTimeout(resolve, firstPaintMaxWaitMs);
    });

    this.firstPaintPromise = Promise.race([
      Promise.all([...this.firstPaintParts.values()].map(({ promise }) => promise)),
      cap,
    ]).then(() => {
      this.firstPaintDone = true;
    });
  }

  firstPaintReady(part) {
    this.firstPaintParts.get(part)?.resolve();
  }

  beginSynchronizedUpdate() {
    this.synchronizedUpdateDepth += 1;
    this.classList.add("main-collection--updating");
    this.setAttribute("aria-busy", "true");
  }

  endSynchronizedUpdate() {
    this.synchronizedUpdateDepth = Math.max(0, this.synchronizedUpdateDepth - 1);

    if (this.synchronizedUpdateDepth === 0 && this.initialReady) {
      this.classList.remove("main-collection--updating");
      this.removeAttribute("aria-busy");
    }
  }

  markCollectionReady() {
    this.initialReady = true;
    this.classList.remove("main-collection--initializing");
    this.classList.add("main-collection--ready");

    if (this.synchronizedUpdateDepth === 0) {
      this.classList.remove("main-collection--updating");
      this.removeAttribute("aria-busy");
    }
  }

  isInfiniteMode() {
    return this.loadingMode === "infinite";
  }

  setupInfiniteScroll() {
    const controls = this.querySelector("[data-infinite-controls]");

    this.infiniteObserver?.disconnect();
    this.infiniteObserver = null;

    if (!this.isInfiniteMode()) {
      if (controls) controls.hidden = true;
      return;
    }

    if (controls) controls.hidden = false;

    this.updateInfiniteControls();
  }

  async initializeCollectionPage() {
    // Keep the initial grid unresolved until products AND interstitials are in
    // their final DOM order. This prevents banners from popping in after cards.
    this.initialReady = false;
    this.productGrid.classList.remove("grouping-resolved");
    this.classList.remove("grouping-resolved");

    initializeApiFacets(this)
      .catch((error) => {
        console.error("[PLP] Failed initializing API facets:", error);
      })
      // No filter sidebar on the page, or it never rendered: stop waiting.
      .finally(() => this.firstPaintReady("filters"));

    if (!this.searchQuery) {
      this.refreshAccurateGroupedCount();
    }

    // Search: the API results always take over the grid (see
    // handleApiFilteredProducts), so don't also page/group the Liquid /search
    // results underneath them — the two racing is what left the first chunk
    // short of 20 or padded with blank cards. Hold a skeleton until the API
    // render replaces it.
    if (this.searchQuery) {
      this.showApiLoadingSkeleton(loadMoreChunkSize);
      this.revealGrid();
      this.markCollectionReady();

      return;
    }

    try {
      if (this.isInfiniteMode()) {
        await this.goToPage(1);
        const productLimit = this.getInitialProductLimit();
        this.infiniteVisibleCount = Math.min(productLimit, this.allCards().length);
        this.setVisibleRange(0, this.infiniteVisibleCount);

        // Interstitials must be inserted before the grid becomes visible so
        // products + banners appear in their correct sequence on first paint.
        this.refreshInterstitials();

        this.updateUrlForPage();
        this.renderPagination();
        this.updateInfiniteControls();
      } else {
        await this.goToPage(this.readPageFromUrl());
      }
    } catch (error) {
      console.error("[PLP] Failed initializing grouped grid:", error);
    } finally {
      // Reveal once, only after the final product/interstitial order exists.
      this.revealGrid();
      this.markCollectionReady();
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.infiniteObserver?.disconnect();

    this.destroyInterstitial();
  }

  destroyInterstitial() {
    if (!this.plpInterstitial) {
      return;
    }

    this.plpInterstitial.destroy();
    this.plpInterstitial = null;
  }

  initInterstitials() {
    if (this.plpInterstitial) {
      this.plpInterstitial.destroy();
    }

    this.plpInterstitial = new PLPInterstitial(this);
  }

  refreshInterstitials() {
    // Filtered/search/sorted API results are always product-only.
    if (this.suppressInterstitials || this.apiFilterMode) {
      this.plpInterstitial?.removeRenderedInterstitials();
      return;
    }

    if (!this.plpInterstitial) {
      this.initInterstitials();
      return;
    }

    this.plpInterstitial.render();
  }

  getInterstitialSlotCountForFirstChunk() {
    if (this.suppressInterstitials || this.apiFilterMode) return 0;

    if (!this.plpInterstitial) {
      this.initInterstitials();
    }

    const columns = Math.max(1, Number(this.plpInterstitial?.getColumnCount?.()) || 1);
    const maxRows = Math.ceil(loadMoreChunkSize / columns);
    const interstitials = this.plpInterstitial?.getActiveInterstitials?.() || [];

    return interstitials
      .filter((config) => Number(config.row) >= 1 && Number(config.row) <= maxRows)
      .reduce((total, config) => total + Math.max(1, Number(config.width) || 1), 0);
  }

  getInitialProductLimit() {
    // Keep the first PLP chunk at 20 visual grid slots. A width-2 banner uses
    // two of those slots, so only 18 product cards are revealed in that case.
    return Math.max(0, loadMoreChunkSize - this.getInterstitialSlotCountForFirstChunk());
  }

  async restoreDefaultProductGrid() {
    if (!this.productGrid || !this.defaultProductGridHtml) {
      return;
    }

    this.renderToken += 1;
    this.loading = true;
    this.apiFilterMode = false;
    this.suppressInterstitials = false;
    this.infiniteVisibleCount = 0;

    this.destroyInterstitial();

    // Hide while rebuilding so the user never sees the intermediate DOM.
    this.productGrid.classList.remove("grouping-resolved");

    this.productGrid.innerHTML = this.defaultProductGridHtml;
    this.productGrid.classList.remove("main-collection__product-grid--api-loading");
    this.apiRenderedProductIds = [];

    this.syncFromDom();

    window.initializeProductCardInteractions(this.productGrid);

    if (this.isInfiniteMode()) {
      await this.goToPage(1);

      const productLimit = this.getInitialProductLimit();

      this.infiniteVisibleCount = Math.min(productLimit, this.allCards().length);

      // Clear All should NOT replay the per-card reveal animation.
      this.allCards().forEach((card, index) => {
        const shouldHide = index >= this.infiniteVisibleCount;

        card.classList.remove(revealClass);
        card.style.removeProperty("--reveal-index");
        card.removeAttribute(pendingRevealAttr);

        if (shouldHide) {
          card.setAttribute("hidden", "");
        } else {
          card.removeAttribute("hidden");
        }
      });

      // Insert interstitial before showing grid.
      this.refreshInterstitials();

      this.updateUrlForPage();
      this.renderPagination();
    } else {
      await this.goToPage(this.readPageFromUrl());

      this.allCards().forEach((card) => {
        card.classList.remove(revealClass);
        card.style.removeProperty("--reveal-index");
        card.removeAttribute(pendingRevealAttr);
      });
    }

    this.loading = false;

    // ONE final paint.
    this.revealGrid();
    this.updateInfiniteControls();
  }

  renderApiPagination() {
    const nav = this.paginationNav();

    if (!nav) {
      return;
    }

    if (this.isInfiniteMode()) {
      nav.hidden = true;
      nav.setAttribute("aria-hidden", "true");
      this.updateInfiniteControls();
      return;
    }

    const cardCount = this.allCards().length;

    const totalPages = Math.max(1, Math.ceil(cardCount / this.pageSize));

    if (totalPages <= 1) {
      nav.hidden = true;

      nav.setAttribute("aria-hidden", "true");

      return;
    }

    nav.hidden = false;

    nav.setAttribute("aria-hidden", "false");

    const prevButton = nav.querySelector("[data-page-prev]");

    const nextButton = nav.querySelector("[data-page-next]");

    if (prevButton) {
      const disabled = this.currentPage <= 1;

      prevButton.disabled = disabled;

      prevButton.setAttribute("aria-disabled", String(disabled));
    }

    if (nextButton) {
      const disabled = this.currentPage >= totalPages;

      nextButton.disabled = disabled;

      nextButton.setAttribute("aria-disabled", String(disabled));
    }

    const numbers = nav.querySelector("[data-pagination-numbers]");

    if (numbers) {
      numbers.innerHTML = this.buildPageNumbersHtml(totalPages);
    }
  }

  showApiLoadingSkeleton(count = 20) {
    if (!this.productGrid) return;

    this.hideApiLoadingSkeleton();

    const fragment = document.createDocumentFragment();
    const skeletonCount = Math.max(4, Math.min(Number(count) || 20, 20));

    for (let index = 0; index < skeletonCount; index += 1) {
      const skeleton = document.createElement("div");
      skeleton.className = "main-collection__product-skeleton";
      skeleton.setAttribute("data-api-product-skeleton", "");
      skeleton.setAttribute("aria-hidden", "true");
      skeleton.innerHTML = `
        <div class="main-collection__product-skeleton-image"></div>
        <div class="main-collection__product-skeleton-line main-collection__product-skeleton-line--title"></div>
        <div class="main-collection__product-skeleton-line main-collection__product-skeleton-line--price"></div>
      `;
      fragment.appendChild(skeleton);
    }

    const templateContainer = this.productGrid.querySelector("[data-plp-interstitial-templates]");
    const anchor = templateContainer || this.paginationNav();

    if (anchor) {
      anchor.before(fragment);
    } else {
      this.productGrid.appendChild(fragment);
    }

    this.productGrid.classList.add("main-collection__product-grid--api-loading");
  }

  hideApiLoadingSkeleton() {
    if (!this.productGrid) return;

    this.productGrid.querySelectorAll("[data-api-product-skeleton]").forEach((element) => element.remove());

    this.productGrid.classList.remove("main-collection__product-grid--api-loading");
  }

  getCardPrimaryImage(card) {
    if (!card) return null;

    return (
      card.querySelector("[data-product-card-image] img") ||
      card.querySelector(".product-card__image img") ||
      card.querySelector("picture img") ||
      card.querySelector("img")
    );
  }

  isCardVisualReady(card) {
    const image = this.getCardPrimaryImage(card);

    // No image means there is nothing visual to wait for.
    if (!image) return true;

    return image.complete && image.naturalWidth > 0;
  }

  waitForCardVisual(card) {
    if (this.isCardVisualReady(card)) {
      return Promise.resolve();
    }

    const image = this.getCardPrimaryImage(card);

    if (!image) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      image.addEventListener("load", resolve, { once: true });
      image.addEventListener("error", resolve, { once: true });
    });
  }

  async revealGroupedProductsInOrder(start, end, token) {
    const cards = this.allCards().slice(start, end);

    // The chunk is about to be on screen: don't leave its images to
    // lazy-loading, which wouldn't start them until they're laid out.
    cards.forEach((card) => {
      card.querySelectorAll("[data-product-card-image] img").forEach((image) => {
        image.loading = "eager";
      });
    });

    // Start waiting on ALL images immediately.
    // Network loading remains concurrent.
    const deadline = new Promise((resolve) => {
      setTimeout(resolve, revealImageDeadlineMs);
    });
    const readyPromises = cards.map((card) => Promise.race([this.waitForCardVisual(card), deadline]));

    for (let index = 0; index < cards.length; index += 1) {
      if (token !== this.renderToken) {
        return;
      }

      // Only visual reveal is sequential.
      // Images themselves have already been loading concurrently.
      await readyPromises[index];

      if (token !== this.renderToken) {
        return;
      }

      const card = cards[index];

      card.removeAttribute("hidden");
      card.removeAttribute(pendingRevealAttr);

      card.classList.remove(revealClass);
      card.classList.add(revealClass);
    }
  }

  prepareGroupedProductReveal(start = 0, end = Infinity) {
    this.allCards().forEach((card, index) => {
      card.classList.remove(revealClass);
      card.style.removeProperty("--reveal-index");

      const isCurrentChunk = index >= start && index < end;

      if (isCurrentChunk) {
        // Current 20 grouped products:
        // reserve position while waiting for ordered reveal.
        card.removeAttribute("hidden");
        card.setAttribute(pendingRevealAttr, "");
      } else {
        // Future products must NOT occupy grid space.
        card.removeAttribute(pendingRevealAttr);
        card.setAttribute("hidden", "");
      }
    });
  }

  async renderApiFilteredProducts({
    products,
    colorActive,
    activeColorFamily,
    specificColorSelected,
    isBackgroundCompletion = false,
  }) {
    if (!this.productGrid || !this.apiProductCardTemplate) {
      return;
    }

    // Matches the Liquid grid and the grouped count, which both skip these.
    products = (products || []).filter((product) => !isExcludedProduct(product));

    // A further cursor page of the same selection (Load More, first-chunk
    // fill): only its new products are added, so the cards already on screen
    // stay put instead of the whole grid going back to a skeleton.
    const productIds = products.map((product) => product.id);
    const renderedIds = this.apiRenderedProductIds || [];
    const extendsRenderedGrid =
      isBackgroundCompletion &&
      this.apiFilterMode &&
      renderedIds.length > 0 &&
      renderedIds.length <= productIds.length &&
      renderedIds.every((id, index) => productIds[index] === id);

    if (extendsRenderedGrid) {
      await this.appendApiFilteredProducts(products.slice(renderedIds.length), {
        colorActive,
        activeColorFamily,
        specificColorSelected,
      });

      this.apiRenderedProductIds = productIds;

      return;
    }

    const requestedPage = this.isInfiniteMode() ? 1 : this.readPageFromUrl();

    this.renderToken += 1;
    this.loading = true;
    this.suppressInterstitials = true;
    this.plpInterstitial?.removeRenderedInterstitials();

    // Keep the PLP occupied while filtered cards are fetched/grouped.
    // Previously removing grouping-resolved here left an empty grid with the
    // Load More control visible for a couple of seconds.
    this.showApiLoadingSkeleton(loadMoreChunkSize);
    this.updateInfiniteControls();

    this.productGrid.classList.remove("grouping-resolved");

    if (this.plpInterstitial) {
      this.plpInterstitial.removeRenderedInterstitials();
    }

    this.apiRenderedProductIds = productIds;

    if (!products?.length) {
      this.productGrid.querySelectorAll("[data-product-card]").forEach((card) => card.remove());

      let noResults = this.productGrid.querySelector(".main-collection__no-results");

      if (!noResults) {
        noResults = document.createElement("div");
        noResults.className = "main-collection__no-results";
        noResults.textContent = "No products found.";

        const anchor = this.productGrid.querySelector("[data-plp-interstitial-templates]") || this.paginationNav();

        if (anchor) {
          anchor.before(noResults);
        } else {
          this.productGrid.appendChild(noResults);
        }
      }

      noResults.hidden = false;

      this.setProductCount(0);
      this.currentPage = 1;
      this.infiniteVisibleCount = 0;
      this.renderApiPagination();
      this.updateInfiniteControls();

      this.hideApiLoadingSkeleton();
      this.productGrid.classList.add("grouping-resolved");
      this.classList.add("grouping-resolved");

      this.loading = false;
      this.updateInfiniteControls();

      return;
    }

    this.productGrid.querySelector(".main-collection__no-results")?.remove();

    this.productGrid.querySelectorAll("[data-product-card]").forEach((card) => {
      card.remove();
    });

    const serverCardToken = this.renderToken;
    const inserted = await this.insertApiProductCards(products, { colorActive, pendingReveal: true });

    if (!inserted || serverCardToken !== this.renderToken) {
      return;
    }

    await this.groupGrid(colorActive ? { activeColorFamily, specificColorSelected } : {});

    // Group first, then initialise interactions only on cards that survive the
    // grouping pass. This avoids wiring event handlers onto cards immediately
    // removed/merged by the PLP grouping logic.
    window.initializeProductCardInteractions(this.productGrid);

    const groupedCardCount = this.allCards().length;

    // The currently hydrated filtered cards are only a partial result while
    // Storefront still has another cursor page. Never present that partial
    // number as the final filtered count.
    if (Number.isFinite(this.resolvedFilteredGroupedProductCount)) {
      this.setProductCount(this.resolvedFilteredGroupedProductCount, {
        filtered: true,
      });
    } else {
      this.setScanningProductCount(groupedCardCount);
    }

    const pageCount = Math.max(1, Math.ceil(groupedCardCount / this.pageSize));

    let revealStart = 0;
    let revealEnd = 0;

    if (this.isInfiniteMode()) {
      const preservedVisible = isBackgroundCompletion ? this.infiniteVisibleCount : 0;

      this.infiniteVisibleCount = Math.min(groupedCardCount, Math.max(loadMoreChunkSize, preservedVisible));

      this.currentPage = 1;

      revealStart = 0;
      revealEnd = this.infiniteVisibleCount;
    } else {
      this.currentPage = Math.min(requestedPage, pageCount);

      revealStart = (this.currentPage - 1) * this.pageSize;

      revealEnd = this.currentPage * this.pageSize;
    }

    const renderToken = this.renderToken;

    this.prepareGroupedProductReveal(revealStart, revealEnd);

    this.hideApiLoadingSkeleton();
    this.revealGrid();

    this.updateUrlForPage();
    this.renderApiPagination();

    this.loading = false;
    this.updateInfiniteControls();

    this.revealGroupedProductsInOrder(revealStart, revealEnd, renderToken);
  }

  // Builds cards for products and adds them at the end of the grid. Resolves
  // false when a newer render took over while server cards were loading.
  async insertApiProductCards(products, { colorActive = false, pendingReveal = false } = {}) {
    const token = this.renderToken;
    const fragment = document.createDocumentFragment();

    // Home Try-On and swatch products need their real server-rendered card
    // (size panel + HTO variants, swatch add-to-bag, free swatch price), which
    // a clone of the generic template card doesn't have — its size panel would
    // add the template's product instead.
    const serverCards = await this.fetchServerProductCards(products);

    if (token !== this.renderToken) {
      return false;
    }

    products.forEach((product) => {
      let card = serverCards.get(product.id);

      if (card) {
        this.apiProductCardTemplate.classList.forEach((className) => card.classList.add(className));
      } else {
        card = this.apiProductCardTemplate.cloneNode(true);

        hydrateApiProductCard(card, product, { childMode: colorActive });
      }

      // New cards stay out of view until the caller's visible range shows them.
      if (pendingReveal) {
        card.setAttribute(pendingRevealAttr, "");
      } else {
        card.removeAttribute(pendingRevealAttr);
        card.setAttribute("hidden", "");
      }

      fragment.appendChild(card);
    });

    const anchor = this.productGrid.querySelector("[data-plp-interstitial-templates]") || this.paginationNav();

    if (anchor) {
      anchor.before(fragment);
    } else {
      this.productGrid.appendChild(fragment);
    }

    return true;
  }

  async appendApiFilteredProducts(products, { colorActive, activeColorFamily, specificColorSelected }) {
    const token = this.renderToken;

    if (products.length) {
      const inserted = await this.insertApiProductCards(products, { colorActive });

      if (!inserted || token !== this.renderToken) {
        return;
      }

      await this.groupGrid(colorActive ? { activeColorFamily, specificColorSelected } : {});

      window.initializeProductCardInteractions(this.productGrid);
    }

    if (Number.isFinite(this.resolvedFilteredGroupedProductCount)) {
      this.setProductCount(this.resolvedFilteredGroupedProductCount, { filtered: true });
    } else {
      this.setScanningProductCount(this.allCards().length);
    }

    // Same rule as a full render: at least the first chunk is shown, so the
    // pages that fill it (search) appear; Load More widens it further itself.
    if (this.isInfiniteMode()) {
      this.infiniteVisibleCount = Math.min(
        this.allCards().length,
        Math.max(loadMoreChunkSize, this.infiniteVisibleCount),
      );

      this.setVisibleRange(0, this.infiniteVisibleCount);
    } else {
      const start = (this.currentPage - 1) * this.pageSize;

      this.setVisibleRange(start, start + this.pageSize);
    }

    this.renderApiPagination();
    this.updateInfiniteControls();
  }

  async fetchServerProductCards(nodes = []) {
    const cards = new Map();

    const eligible = nodes.filter(
      (node) => node?.handle && (isSwatchProductFromTags(node.tags) || isHomeTryOnType(node.productType)),
    );

    await Promise.all(
      eligible.map(async (node) => {
        const card = await this.fetchServerProductCard(node.handle);

        if (card) {
          cards.set(node.id, card);
        }
      }),
    );

    return cards;
  }

  // Renders components/product-card/section.product-card-render.liquid for
  // the product via the Section Rendering API. Cached per handle; each caller
  // gets its own copy of the card.
  fetchServerProductCard(handle) {
    this.serverCardCache ||= new Map();

    if (!this.serverCardCache.has(handle)) {
      const root = window.Shopify?.routes?.root || "/";

      const request = fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${serverCardSectionId}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Product card request failed with ${response.status}`);
          }

          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, "text/html").querySelector("[data-product-card]"))
        .catch((error) => {
          console.error(`[PLP] Failed rendering product card for ${handle}:`, error);
          this.serverCardCache.delete(handle);

          return null;
        });

      this.serverCardCache.set(handle, request);
    }

    return this.serverCardCache.get(handle).then((card) => (card ? document.importNode(card, true) : null));
  }

  enqueueApiFilteredProducts(detail) {
    this.apiRenderChain = this.apiRenderChain
      .catch((error) => {
        console.error("[PLP] Previous API render failed:", error);
      })
      .then(() => this.handleApiFilteredProducts(detail));

    return this.apiRenderChain;
  }

  async handleApiFilteredProducts({
    products,
    pageInfo,
    filters,
    storefrontFilters,
    selectedFilters,
    clientPriceFilter = false,
    hasActiveFilters,
    colorActive,
    activeColorFamily,
    specificColorSelected,
    isBackgroundCompletion = false,
  }) {
    this.apiProducts = products || [];
    this.apiPageInfo = pageInfo || null;
    this.apiFilters = filters || [];
    this.apiStorefrontFilters = storefrontFilters || [];
    this.clientPriceFilterActive = Boolean(clientPriceFilter);

    if (this.searchQuery) {
      this.apiFilterMode = true;

      // storefrontFilters omits search's tag selections (sent in the query
      // string), so key the count on every selected value (price includes its
      // min/max).
      // The "search-selection" prefix keeps these keys distinct from counts
      // older code stored for search under the Storefront-filters key.
      const countFilters = [
        "search-selection",
        ...(selectedFilters || []).map(({ key, value }) => `${key}=${JSON.stringify(value)}`).sort(),
      ];
      const countKey = this.groupedCountCacheKey(countFilters);

      if (this.activeFilteredCountKey !== countKey) {
        this.activeFilteredCountKey = countKey;
        this.resolvedFilteredGroupedProductCount = null;
        this.searchScanningCount = 0;
      }

      await this.renderApiFilteredProducts({
        products: this.apiProducts,
        colorActive,
        activeColorFamily,
        specificColorSelected,
        isBackgroundCompletion,
      });

      this.refreshAccurateGroupedCount(countFilters, { filtered: true }).then((count) => {
        if (Number.isFinite(count) && this.activeFilteredCountKey === countKey) {
          this.resolvedFilteredGroupedProductCount = count;
          this.setProductCount(count, { filtered: true });
          this.updateInfiniteControls();
        }
      });

      return;
    }

    if (!hasActiveFilters) {
      this.refreshAccurateGroupedCount([]);

      if (this.apiFilterMode) {
        await this.restoreDefaultProductGrid();
      }

      this.updateInfiniteControls();

      return;
    }

    this.apiFilterMode = true;

    // Price filtered client-side isn't in storefrontFilters; add the selected
    // range so the count is keyed on it and scans the client-filtered set
    // (see fetchGroupedCountProducts).
    const countFilters = this.clientPriceFilterActive
      ? [
          ...this.apiStorefrontFilters,
          {
            clientPrice: (selectedFilters || [])
              .filter(({ value }) => value?.price)
              .map(({ key }) => key)
              .sort(),
          },
        ]
      : this.apiStorefrontFilters;

    const countKey = this.groupedCountCacheKey(countFilters);
    const filterChanged = this.activeFilteredCountKey !== countKey;

    if (filterChanged) {
      this.activeFilteredCountKey = countKey;
      this.resolvedFilteredGroupedProductCount = null;
    }

    await this.renderApiFilteredProducts({
      products: this.apiProducts,
      colorActive,
      activeColorFamily,
      specificColorSelected,
      isBackgroundCompletion,
    });

    // Compute the real filtered grouped total independently of the progressive
    // product pages used by the grid. This prevents 41 loaded cards from being
    // shown as a final "41 items" count when more filtered products exist.
    this.refreshAccurateGroupedCount(countFilters, { filtered: true }).then((count) => {
      if (Number.isFinite(count) && this.apiFilterMode && this.activeFilteredCountKey === countKey) {
        this.resolvedFilteredGroupedProductCount = count;
        this.setProductCount(count, { filtered: true });
        this.updateInfiniteControls();
      }
    });
  }

  groupedCountCacheKey(filters = []) {
    try {
      return JSON.stringify(filters || []);
    } catch (_) {
      return String(filters?.length || 0);
    }
  }

  groupedCountStorageKey(filters = []) {
    const scope = this.collectionHandle || this.searchQuery || "collection";

    return `plp-grouped-count::${scope}::${this.groupedCountCacheKey(filters)}`;
  }

  readStoredGroupedCount(filters = []) {
    const storageKey = this.groupedCountStorageKey(filters);
    const now = Date.now();

    const read = (storage, ttl) => {
      try {
        const raw = storage?.getItem(storageKey);

        if (!raw) return null;

        const { count, ts } = JSON.parse(raw);

        if (!Number.isFinite(count) || !Number.isFinite(ts) || now - ts > ttl) {
          return null;
        }

        return count;
      } catch (_) {
        return null;
      }
    };

    // Session cache is the freshest source. The longer-lived local cache
    // makes repeat collection visits display the grouped count immediately.
    return read(window.sessionStorage, groupedCountTtlMs) ?? read(window.localStorage, groupedCountPersistentTtlMs);
  }

  storeGroupedCount(filters = [], count) {
    if (!Number.isFinite(count)) {
      return;
    }

    const payload = JSON.stringify({
      count: Math.max(0, Math.trunc(count)),
      ts: Date.now(),
    });
    const storageKey = this.groupedCountStorageKey(filters);

    [window.sessionStorage, window.localStorage].forEach((storage) => {
      try {
        storage.setItem(storageKey, payload);
      } catch (_) {
        // Storage can be unavailable in private/restricted browser modes.
      }
    });
  }

  setScanningProductCount(count) {
    const countElement = document.querySelector('[data-render="product-count"]');

    if (!countElement || !Number.isFinite(count)) {
      return;
    }

    const normalized = Math.max(0, Math.trunc(count));

    // Search: both the grid (loaded cards) and the background count scan
    // report progress here — only ever move the number up.
    if (this.searchQuery) {
      if (normalized < (this.searchScanningCount || 0)) {
        return;
      }

      this.searchScanningCount = normalized;
    }

    // This is a monotonic lower bound while the cursor scan is still running.
    // The trailing + prevents a partial scan from looking like the final total.
    countElement.textContent = `${normalized.toLocaleString("en-US")}+ items`;
    this.classList.add("count-resolved");
  }

  setProductCount(count, { filtered = false } = {}) {
    const countElement = document.querySelector('[data-render="product-count"]');

    if (!countElement || !Number.isFinite(count)) {
      return;
    }

    const normalized = Math.max(0, Math.trunc(count));

    const template = normalized === 1 ? countElement.dataset.countSingular : countElement.dataset.countPlural;

    if (template) {
      countElement.textContent = template.replace(/\d[\d,]*/g, normalized.toLocaleString("en-US"));
    } else {
      countElement.textContent = `${normalized.toLocaleString("en-US")} ` + `${normalized === 1 ? "item" : "items"}`;
    }

    this.hasResolvedProductCount = true;

    if (!filtered) {
      this.resolvedGroupedProductCount = normalized;
    }

    this.classList.add("count-resolved");

    this.updateInfiniteControls?.();
  }

  // Resolves the verified (Product Group) count; onPredicted gets the tag-guess
  // count first, as soon as the product scan ends.
  async fetchGroupedCountProducts(filters = [], { onProgress = null, onPredicted = null } = {}) {
    if (!this.collectionHandle && !this.searchQuery) {
      return 0;
    }

    // Search is always scanned through api-facets' own request (its tag
    // selections live in the query string), so from here on this is a
    // collection.
    if (this.searchQuery || filters.some((filter) => filter?.clientPrice)) {
      return this.fetchClientFilteredGroupedCount({ onProgress, onPredicted });
    }

    // Unfiltered collection: the same scan that indexes style group members.
    if (!filters?.length && !this.isAllProductsCollection) {
      onPredicted?.(await this.styleGroupIndex.count({ onProgress }));

      return this.styleGroupIndex.verifiedCount();
    }

    // Filtered collection, or "all" (no collection(handle:) on the
    // Storefront API, so it counts through search "*").
    const counter = createGroupedCollectionCounter();
    const variables = { filters: filters?.length ? filters : null, first: 250 };

    const { completed, truncated } = await forEachConnectionPage({
      request: (after) =>
        storefront.request(
          this.isAllProductsCollection
            ? { query: SEARCH_GROUPED_COUNT_QUERY, variables: { ...variables, query: "*", after } }
            : {
                query: COLLECTION_GROUPED_COUNT_QUERY,
                variables: { ...variables, handle: this.collectionHandle, after },
              },
        ),
      getConnection: (response) =>
        this.isAllProductsCollection ? response?.data?.search : response?.data?.collection?.products,
      maxPages: maxGroupedCountPages,
      onPage: (nodes, progress) => {
        onProgress?.(counter.addProducts(nodes), progress);
      },
    });

    if (!completed) {
      console.error("[PLP count] Product connection missing:", { collectionHandle: this.collectionHandle });
    }

    if (truncated) {
      console.warn(
        `[PLP count] Grouped count fetch hit the ${maxGroupedCountPages}-page safety cap; results beyond ${
          maxGroupedCountPages * 250
        } products are truncated.`,
      );
    }

    onPredicted?.(counter.count);

    return counter.verify();
  }

  // Search's tag filters (in the query string) and price outside the primary
  // market (client-side) aren't Storefront productFilters, so the count has to
  // come from the same filtered product set api-facets renders.
  async fetchClientFilteredGroupedCount({ onProgress = null, onPredicted = null } = {}) {
    const apiFacets = this.querySelector("[data-api-filters]")?._apiFacets;

    if (!apiFacets) {
      return null;
    }

    const counter = createGroupedCollectionCounter();

    const completed = await apiFacets.collectSearchCountProducts({
      onPage: (nodes, progress) => {
        onProgress?.(counter.addProducts(nodes), progress);
      },
    });

    if (!completed) {
      return null;
    }

    onPredicted?.(counter.count);

    return counter.verify();
  }

  // Unfiltered collection counts only: Liquid's collection.products_count
  // counts active, published products only, and grouping can only merge
  // products, so the grouped count can never be higher. A Storefront scan
  // or stored count above it includes products the grid never renders
  // (draft/unpublished), so it's discarded.
  isUnfilteredCollectionCount(filters = []) {
    return !this.searchQuery && !filters?.length;
  }

  exceedsActiveProductCount(count, filters = []) {
    return (
      this.isUnfilteredCollectionCount(filters) &&
      this.rawProductsCount > 0 &&
      Number.isFinite(count) &&
      count > this.rawProductsCount
    );
  }

  // Once every Liquid page is in the grid, the grouped cards themselves are
  // the exact count of active products, so that wins over the Storefront scan.
  fullyLoadedGridCount() {
    if (this.searchQuery || this.apiFilterMode || !this.gridGroupingSettled || !this.isFullyLoaded()) {
      return null;
    }

    return this.allCards().length;
  }

  reconcileCountWithLoadedGrid() {
    const gridCount = this.fullyLoadedGridCount();

    if (!Number.isFinite(gridCount) || gridCount === this.resolvedGroupedProductCount) {
      return;
    }

    this.groupedCountToken += 1;
    this.groupedCountCache.set(this.groupedCountCacheKey([]), gridCount);
    this.storeGroupedCount([], gridCount);
    this.setProductCount(gridCount);
  }

  async refreshAccurateGroupedCount(filters = [], { filtered = false } = {}) {
    const key = this.groupedCountCacheKey(filters);

    if (this.isUnfilteredCollectionCount(filters)) {
      const gridCount = this.fullyLoadedGridCount();

      if (Number.isFinite(gridCount)) {
        this.reconcileCountWithLoadedGrid();

        return gridCount;
      }
    }

    const cached = this.groupedCountCache.get(key);

    if (Number.isFinite(cached)) {
      this.setProductCount(cached, { filtered });

      return cached;
    }

    const stored = this.readStoredGroupedCount(filters);

    if (Number.isFinite(stored) && !this.exceedsActiveProductCount(stored, filters)) {
      this.groupedCountCache.set(key, stored);
      this.setProductCount(stored, { filtered });

      return stored;
    }

    const existingPromise = this.groupedCountPromises.get(key);

    if (existingPromise) {
      return existingPromise;
    }

    // Search already shows a running "N+ items" from the rendered grid; keep it
    // visible and let the scan grow it instead of blanking to a loader.
    if (!this.hasResolvedProductCount && !this.searchQuery) {
      this.classList.remove("count-resolved");
    }

    const token = ++this.groupedCountToken;

    const promise = (async () => {
      try {
        const count = await this.fetchGroupedCountProducts(filters, {
          onProgress: (progressiveCount, { hasNextPage }) => {
            if (token === this.groupedCountToken && hasNextPage && progressiveCount > 0) {
              this.setScanningProductCount(progressiveCount);
            }
          },
          // Tag-guess total while the Product Groups verify; the verified
          // count below replaces it (and is the one cached).
          onPredicted: (predictedCount) => {
            if (
              token === this.groupedCountToken &&
              Number.isFinite(predictedCount) &&
              !this.exceedsActiveProductCount(predictedCount, filters)
            ) {
              this.setProductCount(predictedCount, { filtered });
            }
          },
        });

        // Search scan was superseded by a newer filter selection.
        if (count === null) {
          return null;
        }

        // The grid finished loading while the scan ran; its count already won.
        if (this.isUnfilteredCollectionCount(filters) && Number.isFinite(this.fullyLoadedGridCount())) {
          return this.fullyLoadedGridCount();
        }

        if (this.exceedsActiveProductCount(count, filters)) {
          console.warn(
            `[PLP count] Storefront scan found ${count} grouped products, more than the ${this.rawProductsCount} active products in the collection; ignoring it.`,
          );

          return null;
        }

        this.groupedCountCache.set(key, count);
        this.storeGroupedCount(filters, count);

        if (token === this.groupedCountToken) {
          this.setProductCount(count, { filtered });
        }

        return count;
      } catch (error) {
        console.error("[PLP count] Failed computing grouped count:", error);

        return null;
      } finally {
        this.groupedCountPromises.delete(key);
      }
    })();

    this.groupedCountPromises.set(key, promise);

    return promise;
  }

  readPageFromUrl() {
    const raw = new URLSearchParams(window.location.search).get(groupedPageParam);
    const parsed = Number.parseInt(raw, 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
  }

  updateUrlForPage() {
    const params = new URLSearchParams(window.location.search);

    if (this.isInfiniteMode()) {
      params.delete(groupedPageParam);
    } else if (this.currentPage > 1) {
      params.set(groupedPageParam, String(this.currentPage));
    } else {
      params.delete(groupedPageParam);
    }

    const query = params.toString();
    const url = `${window.location.pathname}` + `${query ? `?${query}` : ""}` + `${window.location.hash}`;

    history.replaceState(history.state, "", url);
  }

  paginationNav() {
    return this.querySelector("[data-pagination-nav]");
  }

  allCards() {
    return Array.from(this.productGrid.querySelectorAll("[data-product-card]"));
  }

  revealedCount() {
    return this.productGrid.querySelectorAll(`[data-product-card]:not([hidden]):not([${pendingRevealAttr}])`).length;
  }

  hasMorePages() {
    return this.pagesLoaded < this.totalPages;
  }

  isFullyLoaded() {
    return !this.hasMorePages();
  }

  knownPageCount() {
    return Math.max(1, Math.ceil(this.allCards().length / this.pageSize));
  }

  displayPageCount() {
    const known = this.knownPageCount();

    return this.isFullyLoaded() ? known : known + 1;
  }

  // Only this section's HTML (Section Rendering API) — the cards are all we
  // read from it, and it skips rendering the header, footer and the rest.
  pageUrl(pageNumber) {
    const url = new URL(window.location.href);

    url.searchParams.set("page", String(pageNumber));
    url.searchParams.delete(groupedPageParam);

    if (this.dataset.id) {
      url.searchParams.set("section_id", this.dataset.id);
    }

    return url.href;
  }

  syncFromDom() {
    this.renderToken += 1;
    this.loading = false;
    this.gridGroupingSettled = false;
    this.currentPage = 1;
    // Keyed by URL and kept across restores (Clear All), so rebuilding the
    // unfiltered grid doesn't download its pages again.
    this.pageCache ||= new Map();

    this.productGrid.classList.remove("grouping-resolved");

    if (!this.hasResolvedProductCount) {
      this.classList.remove("count-resolved");
    }

    const cards = this.allCards();

    this.loadedProductIds = new Set(cards.map((card) => card.dataset.productId).filter(Boolean));

    cards.forEach((card) => {
      card.setAttribute(pendingRevealAttr, "");
    });

    const nav = this.paginationNav();

    this.totalPages = Number.parseInt(nav?.dataset.totalPages, 10) || 1;
    this.pagesLoaded = 1;
    this.pageUrlTemplate = nav?.dataset.nextPageUrl || null;
    this.rawProductsCount = Number.parseInt(nav?.dataset.productsCount, 10) || 0;
  }

  handleClick(event) {
    const loadMore = event.target.closest("[data-load-more]");

    if (loadMore && this.isInfiniteMode()) {
      this.loadNextInfiniteChunk(loadMore);
      return;
    }

    if (this.loading) {
      return;
    }

    const prev = event.target.closest("[data-page-prev]");

    const next = event.target.closest("[data-page-next]");

    const numberButton = event.target.closest("[data-page]");

    if (this.apiFilterMode) {
      if (prev) {
        this.goToApiPage(this.currentPage - 1, prev);

        return;
      }

      if (next) {
        this.goToApiPage(this.currentPage + 1, next);

        return;
      }

      if (numberButton) {
        this.goToApiPage(Number.parseInt(numberButton.dataset.page, 10), numberButton);
      }

      return;
    }

    if (prev) {
      this.goToPage(this.currentPage - 1, prev);

      return;
    }

    if (next) {
      this.goToPage(this.currentPage + 1, next);

      return;
    }

    if (numberButton) {
      this.goToPage(Number.parseInt(numberButton.dataset.page, 10), numberButton);
    }
  }

  infiniteHasMore() {
    if (!this.isInfiniteMode()) return false;

    if (this.apiFilterMode) {
      const hasLoadedCards = this.infiniteVisibleCount < this.allCards().length;
      const hasRemotePages = Boolean(this.apiPageInfo?.hasNextPage);

      // Search / client-side price: a further cursor page doesn't guarantee
      // more cards (it can group into ones already shown, or be filtered out
      // client-side), so
      // once the real count is known, only offer Load More below it.
      if (
        (this.searchQuery || this.clientPriceFilterActive) &&
        Number.isFinite(this.resolvedFilteredGroupedProductCount)
      ) {
        return (
          hasLoadedCards || (hasRemotePages && this.infiniteVisibleCount < this.resolvedFilteredGroupedProductCount)
        );
      }

      return hasLoadedCards || hasRemotePages;
    }

    // Every Liquid page is in and every card is shown: nothing is left to
    // load, whatever the count says.
    if (this.isFullyLoaded() && this.infiniteVisibleCount >= this.allCards().length) {
      return false;
    }

    if (Number.isFinite(this.resolvedGroupedProductCount)) {
      return this.infiniteVisibleCount < this.resolvedGroupedProductCount;
    }

    return false;
  }

  updateInfiniteControls() {
    const controls = this.querySelector("[data-infinite-controls]");
    const button = this.querySelector("[data-load-more]");

    if (!controls) return;

    controls.hidden = !this.isInfiniteMode();

    if (!this.isInfiniteMode() || !button) return;

    const hasMore = this.infiniteHasMore();
    // Do not leave Load More floating under an intentionally hidden/rebuilding
    // product grid. It returns as soon as the filtered render completes.
    button.hidden = this.loading || !hasMore;
    button.disabled = this.loading;
    button.setAttribute("aria-busy", String(this.loading));
  }

  async loadNextInfiniteChunk(triggerEl = null) {
    if (!this.isInfiniteMode() || this.loading || !this.infiniteHasMore()) {
      return;
    }

    this.loading = true;
    this.updateInfiniteControls();
    triggerEl?.setAttribute("aria-busy", "true");

    try {
      const targetCount = this.infiniteVisibleCount + loadMoreChunkSize;

      if (this.apiFilterMode) {
        const apiFacets = this.querySelector("[data-api-filters]")?._apiFacets;

        while (this.allCards().length < targetCount && this.apiPageInfo?.hasNextPage) {
          const loaded = await apiFacets?.loadNextFilteredPage?.();

          if (!loaded) break;
        }
      } else {
        const token = this.renderToken;

        while (this.allCards().length < targetCount && this.hasMorePages()) {
          const adaptiveBatchSize = this.getAdaptivePageBatchSize(targetCount);
          const consumed = await this.runExclusive(() => this.consumePageBatch(adaptiveBatchSize));

          if (token !== this.renderToken || !consumed) break;

          await this.groupGrid();

          if (token !== this.renderToken) break;
        }
      }

      this.infiniteVisibleCount = Math.min(targetCount, this.allCards().length);

      if (this.apiFilterMode && !Number.isFinite(this.resolvedFilteredGroupedProductCount)) {
        this.setScanningProductCount(this.infiniteVisibleCount);
      }

      this.reconcileCountWithLoadedGrid();
      this.setVisibleRange(0, this.infiniteVisibleCount);
      this.revealGrid();
      this.refreshInterstitials();
      this.updateUrlForPage();
    } catch (error) {
      console.error("[PLP] Failed loading next Load More chunk:", error);
    } finally {
      this.loading = false;
      triggerEl?.removeAttribute("aria-busy");
      this.updateInfiniteControls();
    }
  }

  goToApiPage(pageNumber, triggerEl) {
    const totalPages = Math.max(1, Math.ceil(this.allCards().length / this.pageSize));

    const targetPage = Math.max(1, Math.min(pageNumber, totalPages));

    if (targetPage === this.currentPage) {
      return;
    }

    this.currentPage = targetPage;

    const start = (this.currentPage - 1) * this.pageSize;

    const end = this.currentPage * this.pageSize;

    this.setVisibleRange(start, end);

    this.revealGrid();

    this.updateUrlForPage();

    this.renderApiPagination();

    this.refreshInterstitials();

    if (triggerEl) {
      this.scrollToGridTop();
    }
  }

  getAdaptivePageBatchSize(targetCount) {
    const groupedCount = Math.max(this.allCards().length, 1);
    const rawCount = Math.max(this.loadedProductIds.size, groupedCount);
    const groupedDeficit = Math.max(targetCount - groupedCount, 0);

    if (!groupedDeficit) {
      return 1;
    }

    const rawPerGroupedCard = Math.max(rawCount / groupedCount, 1);
    const estimatedRawNeeded = Math.ceil(groupedDeficit * rawPerGroupedCard * 1.2);
    const rawProductsPerPage = Math.max(Math.round(rawCount / Math.max(this.pagesLoaded, 1)), 1);
    const estimatedPages = Math.ceil(estimatedRawNeeded / rawProductsPerPage);

    return Math.max(1, Math.min(maxAdaptivePageBatchSize, estimatedPages));
  }

  getInitialSeekBatchSize(targetCount) {
    const groupedCount = Math.max(this.allCards().length, 1);
    const rawCount = Math.max(this.loadedProductIds.size, groupedCount);
    const groupedDeficit = Math.max(targetCount - groupedCount, 0);

    if (!groupedDeficit) {
      return 0;
    }

    const rawPerGroupedCard = Math.max(rawCount / groupedCount, 1);
    const rawProductsPerPage = Math.max(Math.round(rawCount / Math.max(this.pagesLoaded, 1)), 1);

    const estimatedRawNeeded = Math.ceil(groupedDeficit * rawPerGroupedCard * 1.35);
    const estimatedPages = Math.ceil(estimatedRawNeeded / rawProductsPerPage);

    return Math.max(1, Math.min(maxInitialSeekPageBatchSize, estimatedPages));
  }

  async goToPage(pageNumber, triggerEl) {
    if (pageNumber < 1) {
      return;
    }

    const token = this.renderToken;
    const previousPage = this.currentPage;

    this.currentPage = pageNumber;
    this.updateUrlForPage();

    this.loading = true;

    triggerEl?.setAttribute("aria-busy", "true");

    if (triggerEl) {
      this.scrollToGridTop();
    }

    if (this.plpInterstitial) {
      this.plpInterstitial.removeRenderedInterstitials();
    }

    const pageStart = (pageNumber - 1) * this.pageSize;
    // Infinite mode's first chunk is 20 grid slots (see getInitialProductLimit),
    // so it never shows cards it's about to hide again.
    const visibleEnd =
      this.isInfiniteMode() && pageNumber === 1 ? this.getInitialProductLimit() : pageNumber * this.pageSize;
    // One past the page, so we know whether there's a next page.
    const targetCount = visibleEnd + 1;

    // Cards are shown as soon as they're grouped, first load included. Later
    // pages only ever append cards (their siblings merge into cards already
    // shown as swatches), so nothing on screen moves as the page fills.
    let lastVisibleCount = -1;

    const showProgress = (start, end) => {
      this.setVisibleRange(start, end);

      const visibleCount = this.revealedCount();

      // render() rebuilds the banners, so only when the visible cards changed.
      if (visibleCount !== lastVisibleCount) {
        lastVisibleCount = visibleCount;
        this.refreshInterstitials();
      }

      this.revealIfAnythingToShow();
    };

    try {
      await this.groupGrid();

      if (token !== this.renderToken) {
        return;
      }

      const targetRangeAlreadyAvailable = this.allCards().length > pageStart;

      if (pageNumber > 1 && !targetRangeAlreadyAvailable) {
        showProgress(0, this.pageSize);
        this.classList.add("grouped-page-seeking");
      } else {
        showProgress(pageStart, visibleEnd);
      }

      if (pageNumber > 1 && this.pagesLoaded === 1 && this.allCards().length < targetCount && this.hasMorePages()) {
        const initialSeekBatchSize = this.getInitialSeekBatchSize(targetCount);

        if (initialSeekBatchSize > 0) {
          const consumed = await this.runExclusive(() => this.consumePageBatch(initialSeekBatchSize));

          if (token !== this.renderToken) {
            return;
          }

          if (consumed) {
            await this.groupGrid();

            if (token !== this.renderToken) {
              return;
            }
          }
        }
      }

      while (this.allCards().length < targetCount && this.hasMorePages()) {
        const adaptiveBatchSize = this.getAdaptivePageBatchSize(targetCount);
        const consumed = await this.runExclusive(() => this.consumePageBatch(adaptiveBatchSize));

        if (token !== this.renderToken) {
          return;
        }

        if (!consumed) {
          break;
        }

        await this.groupGrid();

        if (token !== this.renderToken) {
          return;
        }

        showProgress(pageStart, visibleEnd);
      }

      const knownPageCount = this.knownPageCount();

      this.currentPage = this.isFullyLoaded() && pageNumber > knownPageCount ? knownPageCount : pageNumber;

      const actualStart = (this.currentPage - 1) * this.pageSize;
      const actualEnd = this.currentPage === pageNumber ? visibleEnd : this.currentPage * this.pageSize;

      showProgress(actualStart, actualEnd);
      this.revealGrid();

      this.classList.remove("grouped-page-seeking");

      this.gridGroupingSettled = true;
      this.reconcileCountWithLoadedGrid();
      this.renderPagination();
      this.updateUrlForPage();

      this.prefetchAhead(token);
    } catch (error) {
      if (token === this.renderToken) {
        this.currentPage = previousPage;
        this.updateUrlForPage();
      }

      console.error("[PLP] Failed loading collection page:", error);
    } finally {
      if (token === this.renderToken) {
        triggerEl?.removeAttribute("aria-busy");

        this.loading = false;
        this.classList.remove("grouped-page-seeking");
      }
    }
  }

  // Tag-guess grouping now (no network wait), then the Product Group check in
  // the background.
  async groupGrid(options = {}) {
    await runGroupingPass(this.productGrid, this.styleGroupIndex, options);

    this.scheduleGroupingVerification(options);
  }

  // One check at a time; passes queued behind it collapse into one run with
  // the latest options, since it covers the whole grid.
  scheduleGroupingVerification(options = {}) {
    this.groupingVerifyOptions = options;

    if (this.groupingVerifyQueued) {
      return;
    }

    this.groupingVerifyQueued = true;
    this.groupingVerifyChain = (this.groupingVerifyChain || Promise.resolve()).then(async () => {
      this.groupingVerifyQueued = false;

      const token = this.renderToken;
      const isCurrent = () => token === this.renderToken;

      try {
        const changed = await verifyGrouping(this.productGrid, this.styleGroupIndex, {
          ...this.groupingVerifyOptions,
          isCurrent,
        });

        if (changed && isCurrent()) {
          this.applyRearrangedGrid();
        }
      } catch (error) {
        console.error("[PLP] Failed verifying product grouping:", error);
      }
    });
  }

  // verifyGrouping added/removed cards: re-apply the current view around them.
  // Mid-load, the load in progress applies it when it next shows cards.
  applyRearrangedGrid() {
    window.initializeProductCardInteractions(this.productGrid);

    if (this.loading) {
      return;
    }

    if (this.isInfiniteMode()) {
      this.infiniteVisibleCount = Math.min(this.infiniteVisibleCount, this.allCards().length);
      this.setVisibleRange(0, this.infiniteVisibleCount);
    } else {
      const start = (this.currentPage - 1) * this.pageSize;

      this.setVisibleRange(start, start + this.pageSize);
    }

    this.refreshInterstitials();

    if (this.apiFilterMode) {
      this.renderApiPagination();
    } else {
      this.reconcileCountWithLoadedGrid();
      this.renderPagination();
    }

    this.updateInfiniteControls();
  }

  revealGrid() {
    // Before the first paint: hold the cards as placeholders in their final
    // positions until the filters are ready too.
    if (!this.firstPaintDone) {
      this.productGrid.classList.add("main-collection__product-grid--awaiting-first-paint");

      if (!this.firstPaintRevealQueued) {
        this.firstPaintRevealQueued = true;
        this.firstPaintReady("grid");

        this.firstPaintPromise.then(() => this.revealGrid());
      }

      return;
    }

    this.productGrid.classList.remove("main-collection__product-grid--awaiting-first-paint");
    this.productGrid.classList.add("grouping-resolved");
    this.classList.add("grouping-resolved");
  }

  revealIfAnythingToShow() {
    if (this.revealedCount() > 0) {
      this.revealGrid();
    }
  }

  scrollToGridTop() {
    const rootStyle = getComputedStyle(document.documentElement);
    const headerHeight = Number.parseFloat(rootStyle.getPropertyValue("--header-height")) || 0;
    const promoBarHeight = Number.parseFloat(rootStyle.getPropertyValue("--promo-bar-height")) || 0;
    const targetTop = this.getBoundingClientRect().top + window.scrollY - headerHeight - promoBarHeight - 16;

    animateScrollTo(Math.max(targetTop, 0));
  }

  setVisibleRange(start, end) {
    let revealIndex = 0;

    this.allCards().forEach((card, index) => {
      const shouldHide = index < start || index >= end;

      if (shouldHide) {
        card.setAttribute("hidden", "");
        card.removeAttribute(pendingRevealAttr);
        card.classList.remove(revealClass);
        card.style.removeProperty("--reveal-index");
        return;
      }

      card.removeAttribute("hidden");
      card.removeAttribute(pendingRevealAttr);

      card.classList.remove(revealClass);
      card.style.setProperty("--reveal-index", revealIndex);

      card.classList.add(revealClass);

      revealIndex += 1;
    });
  }

  getPage(pageNumber) {
    const url = this.pageUrl(pageNumber);

    if (!url) {
      return Promise.resolve(null);
    }

    if (this.pageCache.has(url)) {
      return this.pageCache.get(url);
    }

    const request = fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Collection page request failed with ${response.status}`);
        }

        return response.text();
      })
      .then((text) => new DOMParser().parseFromString(text, "text/html"))
      .catch((error) => {
        this.pageCache.delete(url);

        throw error;
      });

    this.pageCache.set(url, request);

    return request;
  }

  async consumePageBatch(batchSize) {
    const startPage = this.pagesLoaded + 1;
    const pageNumbers = [];

    for (let i = 0; i < batchSize && startPage + i <= this.totalPages; i += 1) {
      pageNumbers.push(startPage + i);
    }

    if (!pageNumbers.length) {
      return 0;
    }

    const docs = await Promise.all(
      pageNumbers.map((page) =>
        this.getPage(page).catch((error) => {
          console.error(`[PLP] Failed fetching raw page ${page}:`, error);

          return null;
        }),
      ),
    );

    let consumed = 0;

    for (let i = 0; i < docs.length; i += 1) {
      const doc = docs[i];

      if (!doc) {
        break;
      }

      // Copies: the parsed page stays cached intact for the next restore.
      const newCards = Array.from(doc.querySelectorAll("[data-product-card]"))
        .filter((card) => {
          const productId = card.dataset.productId;

          if (!productId || this.loadedProductIds.has(productId)) {
            return false;
          }

          this.loadedProductIds.add(productId);

          return true;
        })
        .map((card) => document.importNode(card, true));

      newCards.forEach((card) => {
        card.setAttribute(pendingRevealAttr, "");
      });

      const templateContainer = this.productGrid.querySelector("[data-plp-interstitial-templates]");
      const anchor = templateContainer || this.paginationNav();

      if (newCards.length && anchor) {
        anchor.before(...newCards);
        window.initializeProductCardInteractions(this.productGrid);
      } else if (newCards.length) {
        this.productGrid.append(...newCards);
        window.initializeProductCardInteractions(this.productGrid);
      }

      consumed += 1;
    }

    if (consumed) {
      this.pagesLoaded += consumed;
    }

    return consumed;
  }

  async prefetchAhead(token) {
    if (!this.pageUrlTemplate || !this.hasMorePages()) {
      return;
    }

    const startPage = this.pagesLoaded + 1;
    const endPage = Math.min(this.totalPages, startPage + prefetchPagesAhead - 1);
    const pageNumbers = [];

    for (let page = startPage; page <= endPage; page += 1) {
      pageNumbers.push(page);
    }

    if (token !== this.renderToken) {
      return;
    }

    await Promise.all(pageNumbers.map((page) => this.getPage(page).catch(() => null)));
  }

  runExclusive(fn) {
    const run = this.consumeChain.then(fn, fn);

    this.consumeChain = run.catch(() => {});

    return run;
  }

  renderPagination() {
    const nav = this.paginationNav();

    if (!nav) {
      return;
    }

    if (this.isInfiniteMode()) {
      nav.hidden = true;
      nav.setAttribute("aria-hidden", "true");
      this.updateInfiniteControls();
      return;
    }

    const groupedCount = this.allCards().length;

    if (this.isFullyLoaded() && groupedCount <= this.pageSize) {
      nav.hidden = true;
      nav.setAttribute("aria-hidden", "true");

      return;
    }

    const total = this.displayPageCount();

    const canGoNext = this.currentPage < total;

    nav.hidden = false;
    nav.setAttribute("aria-hidden", "false");

    const prevButton = nav.querySelector("[data-page-prev]");

    const nextButton = nav.querySelector("[data-page-next]");

    if (prevButton) {
      const disabled = this.currentPage <= 1;

      prevButton.disabled = disabled;

      prevButton.setAttribute("aria-disabled", String(disabled));
    }

    if (nextButton) {
      nextButton.disabled = !canGoNext;

      nextButton.setAttribute("aria-disabled", String(!canGoNext));
    }

    const numbers = nav.querySelector("[data-pagination-numbers]");

    if (numbers) {
      numbers.innerHTML = this.buildPageNumbersHtml(total);
    }
  }

  buildPageNumbersHtml(total) {
    const current = this.currentPage;
    const windowStart = Math.max(1, Math.min(current, Math.max(1, total - paginationWindow + 1)));
    const windowEnd = Math.min(total, windowStart + paginationWindow - 1);

    let html = "";

    if (windowStart > 1) {
      html += this.pageNumberHtml(1);

      if (windowStart > 2) {
        html += `<span class="main-collection__pagination-ellipsis body">…</span>`;
      }
    }

    for (let page = windowStart; page <= windowEnd; page += 1) {
      html += this.pageNumberHtml(page);
    }

    if (windowEnd < total) {
      html += `<span class="main-collection__pagination-ellipsis body">…</span>`;
    }

    return html;
  }

  pageNumberHtml(page) {
    if (page === this.currentPage) {
      return (
        `<span ` +
        `class="main-collection__pagination-number main-collection__pagination-number--current body" ` +
        `aria-current="page">` +
        `${page}` +
        `</span>`
      );
    }

    return (
      `<button ` +
      `type="button" ` +
      `class="main-collection__pagination-number body" ` +
      `data-page="${page}">` +
      `${page}` +
      `</button>`
    );
  }
}

if (!customElements.get("main-collection")) {
  customElements.define("main-collection", MainCollection);
}
